/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @version 1.20.1
 * @since 1.20.1
 */

import { Fragment, memo, useEffect } from 'react';

import { useFormikContext } from 'formik';
import { useSelector } from 'react-redux';

import FormFieldInput from '../../../form/components/FormFieldInput';
import FormFieldInputPhone from '../../../form/components/FormFieldInputPhone';
import FormFieldTextarea from '../../../form/components/FormFieldTextarea';
import {
    makeSelectCreateInquiryTranslation,
    makeSelectTranslationCommon,
} from '../../../internationalization/intSelectors';
import { ECreateInquiryField } from '../../constants';
import { ICreateInquiryGetAttributes, IInquirySectionAndTopics } from '../../types';
import CreateInquiryFieldLabel from '../CreateInquiryFieldLabel';

interface ICreateInquiryAdditionalAttributes extends Pick<IInquirySectionAndTopics, 'contactInfo'> {
    serviceAttributes?: ICreateInquiryGetAttributes['serviceAttributes'];
}

const CreateInquiryAdditionalAttributes = ({ contactInfo, serviceAttributes }: ICreateInquiryAdditionalAttributes) => {

    /* Translation */
    const { start: tInquiryStart } = useSelector(makeSelectCreateInquiryTranslation());
    const translationCommon = useSelector(makeSelectTranslationCommon());

    const { getFieldProps, setFieldValue } = useFormikContext();

    useEffect(() => {
        if (contactInfo?.phone && !getFieldProps(ECreateInquiryField.phone).value) {
            setFieldValue(ECreateInquiryField.phone, contactInfo.phone, true);
        }
        if (contactInfo?.email && !getFieldProps(ECreateInquiryField.email).value) {
            setFieldValue(ECreateInquiryField.email, contactInfo.email, true);
        }
    }, [contactInfo]);

    if (!serviceAttributes || !serviceAttributes.saveAvailable) return null;

    return (
        <Fragment>
            <Fragment>
                <CreateInquiryFieldLabel
                    name={tInquiryStart.fields.description.label}
                    required={!serviceAttributes.additionalAvailable}
                />
                <FormFieldTextarea
                    dataName="inquiry-field-description"
                    name={ECreateInquiryField.description}
                    placeholder={tInquiryStart.fields.description.placeholder}
                    rows={6}
                />
            </Fragment>
            {
                serviceAttributes.additionalAvailable && (
                    <Fragment>
                        <CreateInquiryFieldLabel
                            name={tInquiryStart.fields.phone.label}
                            prompt={tInquiryStart.fields.phone.prompt}
                        />
                        <FormFieldInputPhone
                            dataName="inquiry-field-phone"
                            mask={translationCommon.phoneInput.mask}
                            name={ECreateInquiryField.phone}
                            placeholder={tInquiryStart.fields.phone.placeholder}
                        />

                        <CreateInquiryFieldLabel
                            required
                            name={tInquiryStart.fields.email.label}
                            prompt={tInquiryStart.fields.email.prompt}
                        />
                        <FormFieldInput
                            dataName="inquiry-field-email"
                            name={ECreateInquiryField.email}
                            placeholder={tInquiryStart.fields.email.placeholder}
                        />
                    </Fragment>
                )
            }
        </Fragment>
    );
};

export default memo(CreateInquiryAdditionalAttributes);
